import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  List,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  WarningOutlined,
  CheckOutlined,
  CloseOutlined,
  TeamOutlined,
  HistoryOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  useSupabaseSession,
  parseUserRole,
  isAdminRole,
  isFleetOwnerRole,
  useFleetOperator,
  formatDate,
  formatTime,
  usePortalPermissions,
  requestJobPlatformAssist,
  cancelJobPlatformAssist,
  reassignJobFleetFromAssist,
  listJobLifecycleEvents,
  isJobWritableForFleet,
  isJobOverdueUnscheduled,
  formatDateTime,
} from '@ubus/shared';
import type {
  Job,
  JobLeg,
  JobStatus,
  JobType,
  JobLegType,
  JobSource,
  JobLifecycleEvent,
  FleetOperatorRecommendation,
} from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';
import AdminSectionPage from '../components/AdminSectionPage';
import JobSplitModal from '../components/JobSplitModal';
import PlatformPendingAcceptPanel from '../components/PlatformPendingAcceptPanel';
import FleetOperatorRecommendationList from '../components/FleetOperatorRecommendationList';
import StatusTag from '../components/StatusTag';
import { TableRowActions } from '../components/TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import ForbiddenPage from './ForbiddenPage';
import { localeConfirm } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';

const { Text, Title } = Typography;

/** Job `scheduled` means published to trips — keep UI label distinct from trip status. */
function JobStatusTag({ status, t }: { status: JobStatus; t: TFunction }) {
  if (status === 'scheduled') {
    return <StatusTag status={status} label={t('jobs.published')} color="success" />;
  }
  return <StatusTag status={status} />;
}

const EDITABLE_STATUSES: JobStatus[] = ['unscheduled', 'planning'];

type JobRow = Job & { job_legs: JobLeg[] };

function formatLegTypeLabel(legType: JobLegType, t: TFunction): string {
  if (legType === 'standby_block') return t('jobs.standByBlock');
  if (legType === 'standby_start') return t('jobs.standByStart');
  return t('jobs.transfer');
}

function renderLegSummary(leg: JobLeg, t: TFunction): string {
  if (leg.leg_type === 'standby_block') {
    return `${leg.pickup_address} · ${t('jobs.occupied')} ${formatTime(leg.earliest_start)} – ${leg.end_time ? formatTime(leg.end_time) : '?'}`;
  }
  return `${formatTime(leg.earliest_start)} · ${leg.pickup_address} → ${leg.dropoff_address}`;
}

function jobTimeWindow(legs: JobLeg[]): string {
  if (!legs.length) return '—';
  const first = legs[0];
  const last = legs[legs.length - 1];
  const start = formatTime(first.earliest_start).slice(0, 5);
  const endRaw =
    last.end_time ??
    last.latest_start ??
    last.earliest_start;
  const end = formatTime(endRaw).slice(0, 5);
  return start === end ? start : `${start}–${end}`;
}

function parseDateParam(raw: string | null): Dayjs | null {
  if (raw && dayjs(raw, 'YYYY-MM-DD', true).isValid()) {
    return dayjs(raw, 'YYYY-MM-DD');
  }
  return null;
}

function defaultDateRange(): [Dayjs, Dayjs] {
  const today = dayjs().startOf('day');
  return [today, today.add(1, 'day')];
}

function parseDateRangeFromSearch(params: URLSearchParams): [Dayjs, Dayjs] {
  const from = parseDateParam(params.get('from'));
  const to = parseDateParam(params.get('to'));
  if (from && to) {
    return from.isAfter(to) ? [to, from] : [from, to];
  }
  const single = parseDateParam(params.get('date'));
  // Legacy ?date= means that single calendar day
  if (single) return [single, single];
  return defaultDateRange();
}

function Jobs() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePortalPermissions();
  const canCreate = hasPermission('jobs.create');
  const canEdit = hasPermission('jobs.edit');
  const canDelete = hasPermission('jobs.delete');
  const canOpenDailySchedule = hasPermission('daily_schedule.view');
  const { session, loading: authLoading } = useSupabaseSession();
  const user = session?.user ?? null;
  const isHqAdmin = isAdminRole(parseUserRole(user));
  const isFleetOwner = isFleetOwnerRole(parseUserRole(user));
  const { operatorId, loading: fleetLoading } = useFleetOperator();
  const canAccessPage = isHqAdmin || (isFleetOwner && Boolean(operatorId));
  const lockedFleetOperatorId = isHqAdmin ? null : operatorId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<JobRow[]>([]);
  const [fleetMap, setFleetMap] = useState<Map<string, { fleetNumber: string; capacity: number }>>(
    () => new Map()
  );
  const [maxCapacity, setMaxCapacity] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() =>
    parseDateRangeFromSearch(searchParams)
  );
  const [statusFilter, setStatusFilter] = useState<JobStatus[]>([]);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'hq' | 'client_request' | 'fleet'>('all');
  const [missingCustomerOnly, setMissingCustomerOnly] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'by-vehicle'>('list');
  const [splitJob, setSplitJob] = useState<JobRow | null>(null);
  const [assistFilter, setAssistFilter] = useState<'all' | 'pending_platform'>('all');
  const [assistJob, setAssistJob] = useState<JobRow | null>(null);
  const [assistReason, setAssistReason] = useState('');
  const [assistSubmitting, setAssistSubmitting] = useState(false);
  const [reassignJob, setReassignJob] = useState<JobRow | null>(null);
  const [reassignFleetId, setReassignFleetId] = useState<string | null>(null);
  const [reassignFleetName, setReassignFleetName] = useState<string | null>(null);
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const [historyJob, setHistoryJob] = useState<JobRow | null>(null);
  const [historyEvents, setHistoryEvents] = useState<JobLifecycleEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const dateFrom = dateRange[0].format('YYYY-MM-DD');
  const dateTo = dateRange[1].format('YYYY-MM-DD');
  /** Default date for new orders / daily schedule jump — start of the filter range. */
  const formDefaultDate = dateRange[0];

  const jobsUrlParams = (from: string, to: string): Record<string, string> => ({
    from,
    to,
  });

  useEffect(() => {
    const jobId = searchParams.get('id');
    if (!jobId) return;
    navigate(`/jobs/${jobId}`, { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    // Legacy ?id= redirects to detail; don't rewrite date params meanwhile
    if (searchParams.get('id')) return;
    const fromUrl = searchParams.get('from');
    const toUrl = searchParams.get('to');
    const legacy = searchParams.get('date');
    if (!fromUrl && !toUrl && !legacy) {
      const [start, end] = defaultDateRange();
      setDateRange([start, end]);
      setSearchParams(
        jobsUrlParams(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')),
        { replace: true }
      );
      return;
    }
    const next = parseDateRangeFromSearch(searchParams);
    const nextFrom = next[0].format('YYYY-MM-DD');
    const nextTo = next[1].format('YYYY-MM-DD');
    if (nextFrom !== dateFrom || nextTo !== dateTo) {
      setDateRange(next);
    }
    // Normalize legacy single-day / date= URLs to the resolved range
    if (nextFrom !== fromUrl || nextTo !== toUrl || legacy) {
      setSearchParams(jobsUrlParams(nextFrom, nextTo), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL → state only when searchParams change
  }, [searchParams]);

  const setDateRangeAndUrl = (range: [Dayjs, Dayjs]) => {
    const ordered = range[0].isAfter(range[1]) ? [range[1], range[0]] as [Dayjs, Dayjs] : range;
    setDateRange(ordered);
    setSearchParams(
      jobsUrlParams(ordered[0].format('YYYY-MM-DD'), ordered[1].format('YYYY-MM-DD')),
      { replace: true }
    );
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      let jobsQuery = supabase
        .from('jobs')
        .select('*, job_legs(*)')
        .gte('schedule_date', dateFrom)
        .lte('schedule_date', dateTo)
        .order('schedule_date', { ascending: true })
        .order('created_at', { ascending: true });
      if (lockedFleetOperatorId) {
        jobsQuery = jobsQuery.or(
          `fleet_operator_id.eq.${lockedFleetOperatorId},assist_from_fleet_operator_id.eq.${lockedFleetOperatorId}`
        );
      } else if (assistFilter === 'pending_platform') {
        jobsQuery = jobsQuery.eq('assist_status', 'pending_platform');
      }

      const [jobsRes, vehiclesRes] = await Promise.all([
        jobsQuery,
        supabase.from('vehicles').select('id, fleet_number, capacity').eq('status', 'active'),
      ]);

      if (jobsRes.error) throw jobsRes.error;
      if (vehiclesRes.error) throw vehiclesRes.error;

      const jobs = (jobsRes.data ?? []) as JobRow[];
      for (const job of jobs) {
        job.job_legs = (job.job_legs ?? []).sort((a, b) => a.sequence - b.sequence);
      }
      setRows(jobs);

      const map = new Map<string, { fleetNumber: string; capacity: number }>();
      let maxCap = 0;
      for (const v of vehiclesRes.data ?? []) {
        map.set(v.id, {
          fleetNumber: v.fleet_number ?? v.id.slice(0, 8),
          capacity: v.capacity,
        });
        if (v.capacity > maxCap) maxCap = v.capacity;
      }
      setFleetMap(map);
      setMaxCapacity(maxCap);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, lockedFleetOperatorId, assistFilter]);

  useEffect(() => {
    if (authLoading || fleetLoading || !canAccessPage) return;
    void fetchRows();
  }, [authLoading, fleetLoading, canAccessPage, fetchRows]);

  const summary = useMemo(() => {
    const total = rows.length;
    const unscheduled = rows.filter((r) => r.status === 'unscheduled' || r.status === 'planning')
      .length;
    const overdueUnscheduled = rows.filter((r) => isJobOverdueUnscheduled(r)).length;
    const scheduled = rows.filter((r) => r.status === 'scheduled').length;
    const cancelled = rows.filter((r) => r.status === 'cancelled').length;
    return { total, unscheduled, overdueUnscheduled, scheduled, cancelled };
  }, [rows]);

  const oversizedJobs = useMemo(() => {
    if (maxCapacity <= 0) return [];
    return rows.filter(
      (r) =>
        (r.status === 'unscheduled' || r.status === 'planning') &&
        r.passenger_count > maxCapacity
    );
  }, [rows, maxCapacity]);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (statusFilter.length > 0) {
      next = next.filter((r) => statusFilter.includes(r.status));
    }
    if (sourceFilter === 'hq') {
      next = next.filter((r) => (r.source ?? 'hq') === 'hq');
    } else if (sourceFilter === 'client_request') {
      next = next.filter((r) => r.source === 'client_request');
    } else if (sourceFilter === 'fleet') {
      next = next.filter((r) => r.source === 'fleet');
    }
    if (missingCustomerOnly) {
      next = next.filter(
        (r) =>
          EDITABLE_STATUSES.includes(r.status) &&
          (r.org_id == null || String(r.org_id).trim() === '')
      );
    }
    return next;
  }, [rows, statusFilter, sourceFilter, missingCustomerOnly]);

  const handleAcceptClientRequest = async (job: JobRow) => {
    const { error } = await supabase
      .from('jobs')
      .update({
        acceptance_status: 'accepted',
        updated_at: new Date().toISOString(),
      })
      .eq('id', job.id);
    if (error) {
      message.error(error.message || t('jobs.acceptFailed'));
      return;
    }
    message.success(t('jobs.accepted'));
    await afterMutation(job.id, fetchRows);
  };

  const handleRejectClientRequest = (job: JobRow) => {
    let reason = '';
    localeConfirm(t, {
      title: t('jobs.rejectTitle'),
      content: (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            {job.title}
          </Text>
          <Text style={{ display: 'block', marginBottom: 4 }}>{t('jobs.rejectReasonLabel')}</Text>
          <Input.TextArea
            rows={3}
            placeholder={t('jobs.rejectReasonPlaceholder')}
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: t('jobs.rejectRequest'),
      okType: 'danger',
      onOk: async () => {
        const trimmed = reason.trim();
        if (!trimmed) {
          message.error(t('jobs.rejectReasonRequired'));
          return Promise.reject(new Error('reason required'));
        }
        const { error } = await supabase
          .from('jobs')
          .update({
            acceptance_status: 'rejected',
            rejection_reason: trimmed,
            status: 'cancelled',
            updated_at: new Date().toISOString(),
          })
          .eq('id', job.id);
        if (error) {
          message.error(error.message || t('jobs.rejectFailed'));
          return Promise.reject(error);
        }
        message.success(t('jobs.rejected'));
        await afterMutation(job.id, fetchRows);
      },
    });
  };

  const jobIsFleetWritable = (job: JobRow) => {
    if (isHqAdmin) return canEdit;
    return canEdit && isJobWritableForFleet(job, lockedFleetOperatorId);
  };

  const jobIsFleetOwned = (job: JobRow) =>
    Boolean(lockedFleetOperatorId) && job.fleet_operator_id === lockedFleetOperatorId;

  const handleRequestAssist = async () => {
    if (!assistJob) return;
    setAssistSubmitting(true);
    try {
      await requestJobPlatformAssist(assistJob.id, assistReason.trim() || undefined);
      message.success(t('jobs.assist.requested'));
      setAssistJob(null);
      setAssistReason('');
      await afterMutation(assistJob.id, fetchRows);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('jobs.assist.failed'));
    } finally {
      setAssistSubmitting(false);
    }
  };

  const handleCancelAssist = (job: JobRow) => {
    localeConfirm(t, {
      title: t('jobs.assist.cancel'),
      content: job.title,
      okText: t('jobs.assist.cancel'),
      onOk: async () => {
        try {
          await cancelJobPlatformAssist(job.id);
          message.success(t('jobs.assist.cancelled'));
          await afterMutation(job.id, fetchRows);
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('jobs.assist.failed'));
          return Promise.reject(err);
        }
      },
    });
  };

  const handleConfirmReassign = async () => {
    if (!reassignJob || !reassignFleetId) return;
    setReassignSubmitting(true);
    try {
      await reassignJobFleetFromAssist({
        jobId: reassignJob.id,
        newFleetOperatorId: reassignFleetId,
      });
      message.success(t('jobs.assist.reassigned'));
      setReassignJob(null);
      setReassignFleetId(null);
      setReassignFleetName(null);
      await afterMutation(reassignJob.id, fetchRows);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('jobs.assist.failed'));
    } finally {
      setReassignSubmitting(false);
    }
  };

  const openHistory = async (job: JobRow) => {
    setHistoryJob(job);
    setHistoryLoading(true);
    try {
      const events = await listJobLifecycleEvents(job.id);
      setHistoryEvents(events);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('jobs.assist.failed'));
      setHistoryEvents([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const assistEventLabel = (type: JobLifecycleEvent['event_type']) => {
    if (type === 'assist_requested') return t('jobs.assist.eventAssistRequested');
    if (type === 'assist_cancelled') return t('jobs.assist.eventAssistCancelled');
    return t('jobs.assist.eventFleetReassigned');
  };

  const byVehicleGroups = useMemo(() => {
    const groups = new Map<string, { key: string; title: string; jobs: JobRow[] }>();
    const pending: JobRow[] = [];

    for (const job of rows) {
      if (job.status === 'scheduled' && job.assigned_vehicle_id) {
        const meta = fleetMap.get(job.assigned_vehicle_id);
        const key = job.assigned_vehicle_id;
        const title = meta?.fleetNumber ?? key.slice(0, 8);
        const g = groups.get(key) ?? { key, title, jobs: [] };
        g.jobs.push(job);
        groups.set(key, g);
      } else if (job.status !== 'cancelled') {
        pending.push(job);
      }
    }

    const sorted = [...groups.values()].sort((a, b) => a.title.localeCompare(b.title));
    return { vehicles: sorted, pending };
  }, [rows, fleetMap]);

  const openCreate = () => {
    const date = formDefaultDate.format('YYYY-MM-DD');
    navigate(`/jobs/new?date=${encodeURIComponent(date)}`);
  };

  const openEdit = (job: JobRow) => {
    navigate(`/jobs/${job.id}/edit`);
  };

  const openView = (job: JobRow) => {
    navigate(`/jobs/${job.id}`);
  };

  const handleDelete = (job: JobRow) => {
    localeConfirm(t, {
      title: t('jobs.deleteTitle'),
      content: t('jobs.deleteContent', { title: job.title }),
      okType: 'danger',
      okText: t('jobs.delete'),
      onOk: async () => {
        const { error } = await supabase.from('jobs').delete().eq('id', job.id);
        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
          return;
        }
        message.success(t('jobs.deleted'));
        void fetchRows();
      },
    });
  };

  const goDailySchedule = () => {
    navigate(`/daily-schedule?date=${dateFrom}`);
  };

  if (authLoading || fleetLoading) {
    return (
      <AdminSectionPage
        title={t('jobs.title')}
        subtitle={t('jobs.subtitle')}
        loading
      />
    );
  }

  if (!canAccessPage) {
    return <ForbiddenPage />;
  }

  const columns: TableProps<JobRow>['columns'] = [
    {
      title: t('jobs.columns.jobNumber'),
      dataIndex: 'job_number',
      key: 'job_number',
      width: 150,
      fixed: 'left',
      ellipsis: true,
      render: (v: string | undefined) => v || '—',
    },
    {
      title: t('jobs.columns.date'),
      dataIndex: 'schedule_date',
      key: 'schedule_date',
      width: 120,
      render: (v: string) => formatDate(v),
    },
    {
      title: t('jobs.columns.source'),
      dataIndex: 'source',
      key: 'source',
      width: 88,
      render: (source: JobSource | undefined) => {
        if (source === 'client_request') return t('jobs.sourceClientRequest');
        if (source === 'fleet') return t('jobs.sourceFleet');
        return t('jobs.sourceHq');
      },
    },
    {
      title: t('jobs.columns.title'),
      dataIndex: 'title',
      key: 'title',
      width: 360,
      render: (title: string, record) => {
        const missingCustomer =
          EDITABLE_STATUSES.includes(record.status) &&
          (record.org_id == null || String(record.org_id).trim() === '');
        const isClientPending =
          record.source === 'client_request' &&
          (record.acceptance_status ?? 'pending') === 'pending' &&
          EDITABLE_STATUSES.includes(record.status);
        const statusTags = [
          record.job_type === 'charter_standby' ? (
            <Tag key="standby" color="orange">{t('jobs.standBy')}</Tag>
          ) : null,
          isClientPending ? (
            <Tag key="pending" color="gold">{t('jobs.sourceClientPending')}</Tag>
          ) : null,
          missingCustomer ? (
            <Tag key="missing" color="error">{t('jobs.missingCustomer')}</Tag>
          ) : null,
          record.split_from_job_id ? (
            <Tag key="split" color="blue">{t('jobs.splitChildTag')}</Tag>
          ) : null,
          record.passenger_count > maxCapacity && EDITABLE_STATUSES.includes(record.status) ? (
            <Tag key="oversize" color="orange">{t('jobs.oversizedTag')}</Tag>
          ) : null,
          record.assist_status === 'pending_platform' ? (
            <Tag key="assist" color="purple">{t('jobs.assist.tagPending')}</Tag>
          ) : null,
          record.assist_status === 'reassigned' ? (
            <Tag key="reassigned" color="cyan">{t('jobs.assist.tagReassigned')}</Tag>
          ) : null,
          isFleetOwner &&
          lockedFleetOperatorId &&
          !isJobWritableForFleet(record, lockedFleetOperatorId) ? (
            <Tag key="readonly">{t('jobs.assist.tagReadOnly')}</Tag>
          ) : null,
        ].filter(Boolean);

        return (
          <Space direction="vertical" size={4} style={{ maxWidth: '100%' }}>
            {statusTags.length > 0 ? (
              <Space size={[4, 4]} wrap style={{ rowGap: 4 }}>
                {statusTags}
              </Space>
            ) : null}
            <Text ellipsis={{ tooltip: title }} style={{ maxWidth: 340 }}>
              {title}
            </Text>
          </Space>
        );
      },
    },
    {
      title: t('jobs.columns.type'),
      dataIndex: 'job_type',
      key: 'job_type',
      width: 100,
      render: (jobType: JobType) =>
        ({ school_run: t('jobs.schoolRun'), charter_standby: t('jobs.standBy'), tour: t('jobs.tour'), transfer: t('jobs.transfer') })[jobType] ?? jobType,
    },
    {
      title: t('jobs.columns.fullDayEscort'),
      dataIndex: 'driver_full_day_escort',
      key: 'driver_full_day_escort',
      width: 120,
      filters: [
        { text: t('jobs.fullDayEscortYes'), value: true },
        { text: t('jobs.fullDayEscortNo'), value: false },
      ],
      onFilter: (value, record) => Boolean(record.driver_full_day_escort) === value,
      render: (v: boolean | undefined) =>
        v ? (
          <Tag color="purple">{t('jobs.fullDayEscortYes')}</Tag>
        ) : (
          <Text type="secondary">{t('jobs.fullDayEscortNo')}</Text>
        ),
    },
    {
      title: t('jobs.columns.window'),
      key: 'window',
      width: 110,
      render: (_, record) => jobTimeWindow(record.job_legs ?? []),
    },
    {
      title: t('jobs.columns.pax'),
      dataIndex: 'passenger_count',
      key: 'passenger_count',
      width: 64,
    },
    {
      title: t('jobs.columns.legs'),
      key: 'legs',
      width: 64,
      render: (_, record) => record.job_legs?.length ?? 0,
    },
    {
      title: t('jobs.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status: JobStatus, record: JobRow) => (
        <Space size={4} wrap>
          <JobStatusTag status={status} t={t} />
          {isJobOverdueUnscheduled(record) ? (
            <Tag color="error">{t('jobs.overdueBadge')}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('jobs.columns.vehicle'),
      key: 'vehicle',
      width: 110,
      render: (_, record) => {
        if (record.status === 'scheduled' && record.assigned_vehicle_id) {
          const meta = fleetMap.get(record.assigned_vehicle_id);
          return <Text code>{meta?.fleetNumber ?? record.assigned_vehicle_id.slice(0, 8)}</Text>;
        }
        if (record.status === 'cancelled') {
          return <Text type="secondary">—</Text>;
        }
        return <Text type="secondary">{t('jobs.pendingSchedule')}</Text>;
      },
    },
    {
      title: t('jobs.columns.actions'),
      key: 'actions',
      width: 280,
      render: (_, record) => {
        const writable = jobIsFleetWritable(record);
        const owned = jobIsFleetOwned(record);
        const pendingAssist = record.assist_status === 'pending_platform';
        const showHistory =
          Boolean(record.assist_status && record.assist_status !== 'none') ||
          Boolean(record.assist_from_fleet_operator_id);

        const isClientPending =
          record.source === 'client_request' &&
          (record.acceptance_status ?? 'pending') === 'pending';
        const canSplit =
          writable &&
          maxCapacity > 0 &&
          record.passenger_count > maxCapacity &&
          !record.split_from_job_id;

        return (
          <TableRowActions>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              title={t('jobs.view')}
              onClick={() => openView(record)}
            />
            {EDITABLE_STATUSES.includes(record.status) || showHistory || pendingAssist ? (
              <>
                {isClientPending && writable ? (
                  <>
                    <Button
                      type="link"
                      size="small"
                      icon={<CheckOutlined />}
                      title={t('jobs.acceptRequest')}
                      onClick={() => void handleAcceptClientRequest(record)}
                    />
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<CloseOutlined />}
                      title={t('jobs.rejectRequest')}
                      onClick={() => handleRejectClientRequest(record)}
                    />
                  </>
                ) : null}
                {canSplit ? (
                  <Button type="link" size="small" onClick={() => setSplitJob(record)}>
                    {t('jobs.splitAction')}
                  </Button>
                ) : null}
                {isFleetOwner &&
                owned &&
                writable &&
                EDITABLE_STATUSES.includes(record.status) &&
                !pendingAssist ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<TeamOutlined />}
                    title={t('jobs.assist.request')}
                    onClick={() => {
                      setAssistReason('');
                      setAssistJob(record);
                    }}
                  >
                    {t('jobs.assist.request')}
                  </Button>
                ) : null}
                {isFleetOwner && owned && pendingAssist ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleCancelAssist(record)}
                  >
                    {t('jobs.assist.cancel')}
                  </Button>
                ) : null}
                {isHqAdmin && pendingAssist ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<SwapOutlined />}
                    onClick={() => {
                      setReassignFleetId(null);
                      setReassignFleetName(null);
                      setReassignJob(record);
                    }}
                  >
                    {t('jobs.assist.reassign')}
                  </Button>
                ) : null}
                {showHistory ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<HistoryOutlined />}
                    title={t('jobs.assist.history')}
                    onClick={() => void openHistory(record)}
                  />
                ) : null}
                {writable ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEdit(record)}
                  />
                ) : null}
                {writable && canDelete ? (
                  <Button
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record)}
                  />
                ) : null}
              </>
            ) : null}
          </TableRowActions>
        );
      },
    },
  ];

  return (
    <>
      <AdminListPage
        title={t('jobs.title')}
        subtitle={t('jobs.subtitle')}
        resultCount={filteredRows.length}
        extra={
          <Space>
            {canOpenDailySchedule ? (
              <Button icon={<CalendarOutlined />} onClick={goDailySchedule}>
                {t('jobs.goDailySchedule')}
              </Button>
            ) : null}
            {canCreate ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {t('jobs.newJob')}
              </Button>
            ) : null}
          </Space>
        }
        filters={
          <Space wrap>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(vals) => {
                if (vals?.[0] && vals?.[1]) {
                  setDateRangeAndUrl([vals[0], vals[1]]);
                }
              }}
              allowClear={false}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder={t('jobs.statusPlaceholder')}
              style={{ minWidth: 200 }}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as JobStatus[])}
              options={[
                { value: 'unscheduled', label: t('jobs.pendingSchedule') },
                { value: 'planning', label: t('jobs.planning') },
                { value: 'scheduled', label: t('jobs.published') },
                { value: 'cancelled', label: t('jobs.cancelled') },
              ]}
            />
            <Select
              allowClear
              placeholder={t('jobs.sourcePlaceholder')}
              style={{ minWidth: 180 }}
              value={sourceFilter === 'all' ? undefined : sourceFilter}
              onChange={(v) => setSourceFilter((v as typeof sourceFilter) || 'all')}
              options={[
                { value: 'hq', label: t('jobs.sourceHq') },
                { value: 'fleet', label: t('jobs.sourceFleet') },
                { value: 'client_request', label: t('jobs.sourceClientRequest') },
              ]}
            />
            {isHqAdmin ? (
              <Select
                allowClear
                placeholder={t('jobs.assist.filterAll')}
                style={{ minWidth: 180 }}
                value={assistFilter === 'all' ? undefined : assistFilter}
                onChange={(v) =>
                  setAssistFilter(v === 'pending_platform' ? 'pending_platform' : 'all')
                }
                options={[
                  { value: 'pending_platform', label: t('jobs.assist.filterPending') },
                ]}
              />
            ) : null}
            <Select
              allowClear
              placeholder={t('jobs.missingCustomerFilter')}
              style={{ minWidth: 160 }}
              value={missingCustomerOnly ? 'missing' : undefined}
              onChange={(v) => setMissingCustomerOnly(v === 'missing')}
              options={[{ value: 'missing', label: t('jobs.missingCustomer') }]}
            />
          </Space>
        }
        hasActiveFilters={
          statusFilter.length > 0 ||
          sourceFilter !== 'all' ||
          missingCustomerOnly ||
          assistFilter !== 'all'
        }
        onClearFilters={() => {
          setStatusFilter([]);
          setSourceFilter('all');
          setMissingCustomerOnly(false);
          setAssistFilter('all');
        }}
        summary={
          <Row gutter={[16, 16]} className="admin-stat-grid">
            <Col xs={12} sm={6}>
              <Card className="admin-inner-card admin-inner-card--compact">
                <Statistic title={t('jobs.total')} value={summary.total} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="admin-inner-card admin-inner-card--compact">
                <Statistic
                  title={t('jobs.pendingSchedule')}
                  value={summary.unscheduled}
                  valueStyle={
                    summary.unscheduled > 0
                      ? { color: 'var(--color-warning, #D97706)' }
                      : undefined
                  }
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="admin-inner-card admin-inner-card--compact">
                <Statistic
                  title={t('jobs.published')}
                  value={summary.scheduled}
                  valueStyle={
                    summary.scheduled > 0 ? { color: 'var(--color-accent)' } : undefined
                  }
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="admin-inner-card admin-inner-card--compact">
                <Statistic title={t('jobs.cancelled')} value={summary.cancelled} />
              </Card>
            </Col>
          </Row>
        }
        alerts={
          <>
            {oversizedJobs.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                icon={<WarningOutlined />}
                message={t('jobs.oversizedMessage', { count: oversizedJobs.length, capacity: maxCapacity })}
                description={
                  <Space direction="vertical" size="small">
                    <span>
                      {oversizedJobs.map((j) => j.title).join('; ')}. {t('jobs.oversizedDescription')}
                    </span>
                    {canEdit && oversizedJobs[0] ? (
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => setSplitJob(oversizedJobs[0])}
                      >
                        {t('jobs.splitAction')}
                        {oversizedJobs.length > 1
                          ? ` — ${oversizedJobs[0].title}`
                          : ''}
                      </Button>
                    ) : null}
                  </Space>
                }
              />
            ) : null}
            {summary.overdueUnscheduled > 0 ? (
              <Alert
                type="error"
                showIcon
                icon={<WarningOutlined />}
                message={t('jobs.overdueMessage', { count: summary.overdueUnscheduled })}
                description={t('jobs.overdueDescription')}
                action={
                  canOpenDailySchedule ? (
                    <Button size="small" type="primary" danger onClick={goDailySchedule}>
                      {t('jobs.goDailySchedule')}
                    </Button>
                  ) : null
                }
              />
            ) : null}
            {summary.unscheduled > 0 ? (
              <Alert
                type="info"
                showIcon
                message={t('jobs.unpublishedMessage', { count: summary.unscheduled })}
                description={t('jobs.unpublishedDescription')}
                action={
                  canOpenDailySchedule ? (
                    <Button size="small" type="primary" onClick={goDailySchedule}>
                      {t('jobs.goDailySchedule')}
                    </Button>
                  ) : null
                }
              />
            ) : null}
          </>
        }
      >
        {isFleetOwner ? <PlatformPendingAcceptPanel /> : null}
        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as 'list' | 'by-vehicle')}
          items={[
            {
              key: 'list',
              label: t('jobs.jobList'),
              children: (
                <Table<JobRow>
                  rowKey="id"
                  columns={columns}
                  dataSource={filteredRows}
                  loading={loading}
                  pagination={pagination}
                  onChange={onTableChange}
                  rowClassName={rowClassName}
                  scroll={{ x: 1720 }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {(record.job_legs ?? []).length === 0 ? (
                          <Text type="secondary">{t('jobs.noLegs')}</Text>
                        ) : (
                          record.job_legs.map((leg) => (
                            <div key={leg.id}>
                              <Text strong>#{leg.sequence}</Text>{' '}
                              <Tag
                                color={leg.leg_type === 'standby_block' ? 'orange' : 'blue'}
                              >
                                {formatLegTypeLabel(leg.leg_type, t)}
                              </Tag>{' '}
                              {renderLegSummary(leg, t)}
                              {leg.service_duration_minutes > 0 &&
                                leg.leg_type !== 'standby_block' && (
                                  <Text type="secondary">
                                    {' '}
                                    · {t('jobs.serviceMinutes', { count: leg.service_duration_minutes })}
                                  </Text>
                                )}
                            </div>
                          ))
                        )}
                      </Space>
                    ),
                  }}
                  locale={{
                    emptyText: t('jobs.noJobsForDateRange', {
                      from: formatDate(dateFrom),
                      to: formatDate(dateTo),
                    }),
                  }}
                />
              ),
            },
            {
              key: 'by-vehicle',
              label: t('jobs.byVehicle'),
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {byVehicleGroups.pending.length > 0 && (
                    <Card
                      size="small"
                      title={
                        <Space>
                          <Title level={5} style={{ margin: 0 }}>
                            {t('jobs.pendingSchedule')}
                          </Title>
                          <Tag>{byVehicleGroups.pending.length}</Tag>
                        </Space>
                      }
                    >
                      <List
                        size="small"
                        dataSource={byVehicleGroups.pending}
                        renderItem={(job) => (
                          <List.Item>
                            <Space wrap>
                              <Text type="secondary">{formatDate(job.schedule_date)}</Text>
                              {job.job_type === 'charter_standby' && (
                                <Tag color="orange">{t('jobs.standBy')}</Tag>
                              )}
                              {job.driver_full_day_escort && (
                                <Tag color="purple">{t('jobs.fullDayEscort')}</Tag>
                              )}
                              <Text>{job.title}</Text>
                              <Text type="secondary">
                                {job.passenger_count} {t('jobs.pax')} · {jobTimeWindow(job.job_legs)}
                              </Text>
                              <JobStatusTag status={job.status} t={t} />
                              {isJobOverdueUnscheduled(job) ? (
                                <Tag color="error">{t('jobs.overdueBadge')}</Tag>
                              ) : null}
                            </Space>
                          </List.Item>
                        )}
                      />
                    </Card>
                  )}
                  {byVehicleGroups.vehicles.length === 0 &&
                    byVehicleGroups.pending.length === 0 && (
                      <Text type="secondary">{t('jobs.noJobsForThisDate')}</Text>
                    )}
                  {byVehicleGroups.vehicles.map((g) => (
                    <Card
                      key={g.key}
                      size="small"
                      title={
                        <Space>
                          <Title level={5} style={{ margin: 0 }}>
                            {g.title}
                          </Title>
                          <Tag color="success">{t('jobs.publishedCount', { count: g.jobs.length })}</Tag>
                        </Space>
                      }
                    >
                      <List
                        size="small"
                        dataSource={g.jobs}
                        renderItem={(job) => (
                          <List.Item>
                            <Space wrap>
                              <Text type="secondary">{formatDate(job.schedule_date)}</Text>
                              {job.job_type === 'charter_standby' && (
                                <Tag color="orange">{t('jobs.standBy')}</Tag>
                              )}
                              {job.driver_full_day_escort && (
                                <Tag color="purple">{t('jobs.fullDayEscort')}</Tag>
                              )}
                              <Text>{job.title}</Text>
                              <Text type="secondary">
                                {job.passenger_count} {t('jobs.pax')} · {jobTimeWindow(job.job_legs)}
                              </Text>
                            </Space>
                          </List.Item>
                        )}
                      />
                    </Card>
                  ))}
                </Space>
              ),
            },
          ]}
        />
      </AdminListPage>

      <JobSplitModal
        open={!!splitJob}
        job={splitJob}
        maxCapacity={maxCapacity}
        onCancel={() => setSplitJob(null)}
        onSuccess={() => {
          const id = splitJob?.id;
          setSplitJob(null);
          void afterMutation(id, fetchRows);
        }}
      />

      <Modal
        title={t('jobs.assist.requestTitle')}
        open={!!assistJob}
        onCancel={() => {
          setAssistJob(null);
          setAssistReason('');
        }}
        onOk={() => void handleRequestAssist()}
        confirmLoading={assistSubmitting}
        okText={t('jobs.assist.request')}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('jobs.assist.requestHint')}
        </Text>
        {assistJob ? (
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            {assistJob.title}
          </Text>
        ) : null}
        <Text style={{ display: 'block', marginBottom: 4 }}>{t('jobs.assist.reasonLabel')}</Text>
        <Input.TextArea
          rows={3}
          value={assistReason}
          placeholder={t('jobs.assist.reasonPlaceholder')}
          onChange={(e) => setAssistReason(e.target.value)}
        />
      </Modal>

      <Modal
        title={t('jobs.assist.reassignTitle')}
        open={!!reassignJob}
        onCancel={() => {
          setReassignJob(null);
          setReassignFleetId(null);
          setReassignFleetName(null);
        }}
        onOk={() => void handleConfirmReassign()}
        confirmLoading={reassignSubmitting}
        okText={t('jobs.assist.reassignOk')}
        okButtonProps={{ disabled: !reassignFleetId }}
        width={720}
      >
        {reassignJob ? (
          <>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              {reassignJob.title}
            </Text>
            <FleetOperatorRecommendationList
              jobId={reassignJob.id}
              passengerCount={reassignJob.passenger_count}
              onSelect={(rec: FleetOperatorRecommendation) => {
                setReassignFleetId(rec.fleet_operator_id);
                setReassignFleetName(rec.operator_name);
              }}
            />
            {reassignFleetId ? (
              <Text type="secondary">
                {t('jobs.assist.reassign')}: {reassignFleetName ?? reassignFleetId}
              </Text>
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        title={t('jobs.assist.historyTitle')}
        open={!!historyJob}
        onCancel={() => {
          setHistoryJob(null);
          setHistoryEvents([]);
        }}
        footer={null}
        width={560}
      >
        {historyLoading ? (
          <Text type="secondary">…</Text>
        ) : historyEvents.length === 0 ? (
          <Text type="secondary">{t('jobs.assist.historyEmpty')}</Text>
        ) : (
          <List
            size="small"
            dataSource={historyEvents}
            renderItem={(ev) => (
              <List.Item>
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Text strong>{assistEventLabel(ev.event_type)}</Text>
                  <Text type="secondary">{formatDateTime(ev.created_at)}</Text>
                  {ev.note ? <Text>{ev.note}</Text> : null}
                  {ev.event_type === 'fleet_reassigned' ? (
                    <Text type="secondary">
                      {(ev.from_fleet_operator_id ?? '—').slice(0, 8)} →{' '}
                      {(ev.to_fleet_operator_id ?? '—').slice(0, 8)}
                    </Text>
                  ) : null}
                </Space>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </>
  );
}

export default Jobs;
